import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Spin, Input } from 'antd';
import CurrencyInput from 'react-currency-input-field';
import { Eye, EyeSlash, Plus, MinusCircle } from '@phosphor-icons/react';
import { useOneButtonStore, COMMON_CONSTANTS } from '../../../../store/oneButtonStore';
import { useOneButtonCapability } from '../../../../hooks/useOneButtonCapability';
import masterServices from '../../../../services/oneButtonServices/masterServices';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import { useCurrentUser } from '../../../../contexts/UserContext';
import type { selectedQuote } from './FinancingGridCard';
import './oneButton-css/RoofQuoteDetails.css';
import './oneButton-css/BatteryQuoteDetails.css';
import { GenerateQuoteSectionHead } from './GenerateQuoteSectionHead';
import type { GqSectionStat } from './GenerateQuoteSectionHead.types';
import { OBDSelect } from './form-elements';
import {
    getLightreachBatteryLeaseLeadIdFromOpportunity,
    resolveLightreachBatteryLeaseLeadId,
} from '../../../../helpers/lightReachLeadId';
import {
    lrBoOffersFromResponse,
    lrboEscalationPct,
    lrboMonthly,
    matchLrboProduct,
    pickBatteryEscalatorLikeV1,
    productIdFromLrBoOffer,
    year1TotalMonthlyFromLrBoOffer,
} from '../../../../helpers/lightReachBatteryMonthly';
import { resolveProductTypeIdFromCategory } from './productsFinanceHubHelpers';

function namedTierFromPricingLabel(raw: unknown, isTesla: boolean): string | null {
    const s = String(raw ?? '').trim().toLowerCase();
    if (!s) return null;
    if (isTesla) {
        if (s === 'custom') return 'Custom';
        if (s === 'base' || s.startsWith('base')) return 'Base';
        if (s === 'min' || s.startsWith('min')) return 'Min';
        return null;
    }
    if (s === 'floor') return 'Floor';
    if (s === 'min' || s.startsWith('min')) return 'Min';
    if (s === 'mid' || s.startsWith('mid')) return 'Mid';
    if (s === 'max') return 'Max';
    if (s === 'standard' || s.startsWith('std')) return 'Standard';
    return null;
}

function positiveMoneyOrNull(v: unknown): number | null {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
}

/** V1 direct quote does not treat grid escalator 0 as a priced Lightreach battery offer. */
function isPositiveGridEscalator(value: unknown): boolean {
    if (value == null || value === '') return false;
    const n = Number(value);
    return Number.isFinite(n) && n >= 0;
}

function excelOfferFromObject(o: Record<string, unknown>): Record<string, unknown> | null {
    const excel = o.ExcelOffer ?? o.excelOffer;
    return excel && typeof excel === 'object' ? (excel as Record<string, unknown>) : null;
}

function isRecommendedPricingOffer(o: Record<string, unknown>): boolean {
    return o.Recommended === true || o.recommended === true;
}

/**
 * Pricing API recommended offer ExcelOffer (batteryEPC + batteryAdder).
 * Prefers `Recommended: true` when Offer[] is present (term 10 vs recommended term 12).
 */
function recommendedExcelOfferFromQuote(source: unknown, seen = new Set<object>()): Record<string, unknown> | null {
    if (!source || typeof source !== 'object') return null;
    if (seen.has(source as object)) return null;
    seen.add(source as object);
    const o = source as Record<string, unknown>;

    const offers = o.Offer ?? o.offer;
    if (Array.isArray(offers) && offers.length) {
        const recommended =
            offers.find((item) => item && typeof item === 'object' && isRecommendedPricingOffer(item as Record<string, unknown>))
            ?? null;
        const pick = recommended && typeof recommended === 'object' ? (recommended as Record<string, unknown>) : null;
        if (!pick) return null;
        return excelOfferFromObject(pick) ?? recommendedExcelOfferFromQuote(pick, seen);
    }

    if (o.Recommended === false || o.recommended === false) return null;

    if (isRecommendedPricingOffer(o)) {
        const excel = excelOfferFromObject(o);
        if (excel) return excel;
    }

    for (const key of ['payload', 'escalatorOption', 'EscalatorOption']) {
        const nested = recommendedExcelOfferFromQuote(o[key], seen);
        if (nested) return nested;
    }
    if (Array.isArray(o.products)) {
        for (const p of o.products) {
            const nested = recommendedExcelOfferFromQuote(p, seen);
            if (nested) return nested;
        }
    }
    return null;
}

/** Battery C15 (Tesla) / tier match from recommended ExcelOffer.batteryEPC. */
function pricedBatteryEpcFromQuote(source: unknown): number | null {
    const excel = recommendedExcelOfferFromQuote(source);
    return positiveMoneyOrNull(excel?.batteryEPC ?? excel?.BatteryEPC);
}

function pricedBatteryAdderFromQuote(source: unknown): number {
    const excel = recommendedExcelOfferFromQuote(source);
    const n = Number(excel?.batteryAdder ?? excel?.BatteryAdder ?? 0);
    return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Original Lightreach estimate total: batteryEPC + batteryAdder. */
function pricedBatteryTotalFromQuote(source: unknown): number | null {
    const epc = pricedBatteryEpcFromQuote(source);
    if (epc == null) return null;
    return epc + pricedBatteryAdderFromQuote(source);
}

function amountsMatch(a: number | null | undefined, b: number | null | undefined): boolean {
    if (a == null || b == null || !Number.isFinite(a) || !Number.isFinite(b)) return false;
    return Math.abs(Number(a) - Number(b)) < 1;
}

function matchPricedAmountToTier(
    amount: number,
    tiers: Array<{ key: string; price: number }>,
): string | null {
    const hit = tiers.find((t) => t.price > 0 && Math.abs(amount - t.price) < 1);
    return hit?.key ?? null;
}

function matchSelectOptionByNumber(
    options: { value: string; label: string }[],
    raw: unknown,
): string | null {
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    const found = options.find((o) => Number(o.value) === n || Number(o.label) === n);
    return found ? found.value : null;
}
/**
 * Battery Calculator C4 is selected products, not finance-partner match (V1 OBA-5815).
 * B = battery only, SB = battery + solar, BR = battery + roof, SBR = all three.
 */
function batteryCalculatorProductCode(interest: any): string {
    const hasSolar = !!interest?.Solar;
    const hasBattery = !!(interest?.Battery || interest?.BatteryOnly);
    const hasRoof = !!(interest?.Roof || interest?.RRR || interest?.RoofOnly);

    if (interest?.BatteryOnly || (hasBattery && !hasSolar && !hasRoof)) {
        return 'B';
    }
    if (hasSolar && hasBattery && hasRoof) {
        return 'SBR';
    }
    if (hasSolar && hasBattery) {
        return 'SB';
    }
    if (hasBattery && hasRoof) {
        return 'BR';
    }
    return 'B';
}

const BATTERY_SETUP_ADDER_NAME = 'Battery only Install';
const BATTERY_SETUP_ADDER_ID = 'battery-setup-adder';
const BATTERY_SETUP_ADDER_SERVICE_ID = 'BRW000';

type BatteryAdderCatalogItem = {
    Value: string;
    Text: string;
    Group?: string;
    UnitType?: string;
    Qty?: number;
    UnitPrice?: number;
    FreeQty?: number;
    ServiceID?: string;
    ServiceCode?: string;
    ProductTypeAdderID?: number;
    ProductTypeID?: number | null;
    ProductName?: string;
    disabled?: boolean;
};

type BatteryAdderRow = {
    id: string | number;
    item: string;
    name: string;
    ServiceID: string;
    quantity: number;
    unitPrice: number;
    freeQty: number;
    subTotal: number;
    isFixed?: boolean;
    productTypeAdderID: number;
    productTypeID: number | null;
};

function billableSubtotal(row: BatteryAdderRow) {
    return Math.max(0, Number(row.quantity) - Number(row.freeQty || 0)) * Number(row.unitPrice || 0);
}

function syncSetupAdderRow(rows: BatteryAdderRow[], amount: number): BatteryAdderRow[] {
    const without = rows.filter((r) => !r.isFixed && r.id !== BATTERY_SETUP_ADDER_ID);
    if (amount <= 0) return without;
    return [
        {
            id: BATTERY_SETUP_ADDER_ID,
            item: BATTERY_SETUP_ADDER_NAME,
            name: BATTERY_SETUP_ADDER_NAME,
            ServiceID: BATTERY_SETUP_ADDER_SERVICE_ID,
            quantity: 1,
            unitPrice: amount,
            freeQty: 0,
            subTotal: amount,
            isFixed: true,
            productTypeAdderID: 0,
            productTypeID: null,
        },
        ...without,
    ];
}

function totalsFromAdderRows(rows: BatteryAdderRow[]) {
    let displayTotal = 0;
    let billableTotal = 0;
    const next = rows.map((ele) => {
        if (ele.isFixed) {
            const subTotal = Number(ele.unitPrice) || 0;
            displayTotal += subTotal;
            return { ...ele, quantity: 1, subTotal };
        }
        const subTotal = billableSubtotal(ele);
        displayTotal += subTotal;
        billableTotal += subTotal;
        return { ...ele, subTotal };
    });
    return { rows: next, displayTotal, billableTotal };
}

function userAddersPayload(rows: BatteryAdderRow[]) {
    return rows
        .filter((ele) => !ele.isFixed)
        .map((ele) => ({
            AdderCost: ele.subTotal || 0,
            AdderQuantity: Number(ele.quantity),
            AdderSelect: ele.item,
            Description: '',
            UnitPrice: Number(ele.unitPrice) || 0,
            Commissonable: 'N',
            productTypeAdderID: ele.productTypeAdderID,
            productTypeID: ele.productTypeID,
        }));
}

interface BatteryQuoteDetailsProps {
    confirmQuoteCellKey: selectedQuote | null;
    priceQuote: any;
    theme: string;
    lightreachPriceCapsData: any[];
    lightreachPriceCapsReady?: boolean;
    lightreachBatteryLeaseLeadId?: string | null;
    updateLoaderCount: (loader: boolean) => void;
    updateErrorMessage: (message: string) => void;
    updateBatteryDetails: (batteryDetails: any) => void;
    solarMasterSync?: {
        enabled: boolean;
        termApr?: string;
        pricingMethod?: string;
        escalator?: string;
    };
}

export const BatteryQuoteDetails: React.FC<BatteryQuoteDetailsProps> = ({
    confirmQuoteCellKey,
    priceQuote,
    theme,
    lightreachPriceCapsData,
    lightreachPriceCapsReady = false,
    lightreachBatteryLeaseLeadId = null,
    updateLoaderCount,
    updateErrorMessage,
    updateBatteryDetails,
    solarMasterSync,
}) => {
    const {
        systemDesign,
        opportunityDetails,
        financePartnersList,
        productTypeList,
        // updateBatteryDetails
    } = useOneButtonStore();

    const { hasCapability } = useCurrentUser();
    const { getCapabilityValue } = useOneButtonCapability();
    const addersReadOnly = getCapabilityValue('UI_Utility_Info_Access') === 'View';

    const [enabledFields, setEnabledFields] = useState<string[]>([]);

    // --- Form State ---
    const [financePartnerId, setFinancePartnerId] = useState<string | null>(null);
    const [financeMethodId, setFinanceMethodId] = useState<string | null>(null);
    const [pricingMethod, setPricingMethod] = useState<string | null>(null);
    const [escalator, setEscalator] = useState<string | null>(null);
    const [term, setTerm] = useState<string | null>(null);
    const [price, setPrice] = useState<number>(0);
    const [termApr, setTermApr] = useState<string | null>(null);
    const [rebate, setRebate] = useState<number>(0);
    const [downPayment, setDownPayment] = useState<number>(0);
    const [depositAmount, setDepositAmount] = useState<number>(0);
    const [tax, setTax] = useState<number>(0);
    const [dealerFee, setDealerFee] = useState<number>(0);
    const [offerId, setOfferId] = useState<string | null>(null);
    const [uponCompletionOfPhysicalInstall, setUponCompletionOfPhysicalInstall] = useState<number>(0);
    const [uponCompletionOfWork, setUponCompletionOfWork] = useState<number>(0);

    // --- Battery Selection State ---
    const [priceTier, setPriceTier] = useState<string>('Standard');
    const [batteryAdderCatalog, setBatteryAdderCatalog] = useState<BatteryAdderCatalogItem[]>([]);
    const [selectedAdderValues, setSelectedAdderValues] = useState<string[]>([]);
    const [adderRows, setAdderRows] = useState<BatteryAdderRow[]>([]);
    const adderIdRef = useRef(1);

    // --- Dynamic Options ---
    const [financeMethods, setFinanceMethods] = useState<{ value: string, label: string }[]>([]);
    const [termAprOptions, setTermAprOptions] = useState<{ value: string, label: string }[]>([]);
    const [pricingMethods, setPricingMethods] = useState<{ value: string, label: string }[]>([]);
    const [escalatorOptions, setEscalatorOptions] = useState<{ value: string, label: string }[]>([]);
    const [termOptions, setTermOptions] = useState<{ value: string, label: string }[]>([]);

    // --- Calculated Data ---
    const [detailValue, setDetailValue] = useState<any>({
        grossSalesPrice: 0,
        Financed_Amount: 0,
        totalSalesCommission: 0,
        totalAdderCost: 0,
        batteryCostSubTotal: 0,
        floorTierPrice: 0,
        minTierPrice: 0,
        midTierPrice: 0,
        stdTierPrice: 0,
        Monthly_Finance_Partner_Payment: 0,
        baseTierPrice: 0,
    });

    const [showCommission, setShowCommission] = useState(false);

    /** Latest numeric values for Excel payload without listing them in updateCalculations deps (avoids recalc on every keystroke). */
    const depositAmountRef = useRef(depositAmount);
    depositAmountRef.current = depositAmount;
    const billableAdderCostRef = useRef(0);

    /** Values last applied in a successful Battery Calculator response (for blur dedupe). */
    const lastCalcCurrencyRef = useRef<{ depositAmount: number } | null>(null);
    const calculationInFlightRef = useRef(false);
    const [lightreachEligibleProductDataForBattery, setLightreachEligibleProductDataForBattery] = useState<any[]>([]);
    const lrboLeadIdRef = useRef<string | null>(null);
    const lastLrboAmountRef = useRef<number | null>(null);
    const escalatorRef = useRef(escalator);
    const termRef = useRef(term);
    const escalatorOptionsRef = useRef(escalatorOptions);
    const lrboProductsRef = useRef(lightreachEligibleProductDataForBattery);
    const selectedGridTermRef = useRef<string | null>(null);
    const selectedGridEscalatorRef = useRef<string | null>(null);
    const openedFromPricingOfferRef = useRef(false);
    const pricedBatteryEpcRef = useRef<number | null>(null);
    const pricedTierLabelRef = useRef<string | null>(null);
    escalatorRef.current = escalator;
    termRef.current = term;
    escalatorOptionsRef.current = escalatorOptions;
    lrboProductsRef.current = lightreachEligibleProductDataForBattery;

    const [teslaPriceValue, setTeslaPriceValue] = useState<number | undefined>(undefined);
    const teslaPriceValueRef = useRef<number | undefined>(undefined);
    teslaPriceValueRef.current = teslaPriceValue;
    const priceTierRef = useRef(priceTier);
    priceTierRef.current = priceTier;
    const [teslaCustomAmount, setTeslaCustomAmount] = useState<number>(0);

    const adderTotals = useMemo(() => totalsFromAdderRows(adderRows), [adderRows]);
    const billableAdderCost = adderTotals.billableTotal;
    const adderDisplayTotal = adderTotals.displayTotal;
    billableAdderCostRef.current = billableAdderCost;

    const opportunityProducts = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
    const batteryProductTypeId = resolveProductTypeIdFromCategory(
        COMMON_CONSTANTS.BatteryProductName,
        productTypeList,
        opportunityProducts,
    );

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const raw = await quoteServices.getAdders();
                const mapped: BatteryAdderCatalogItem[] = (raw || [])
                    .filter((x: any) => String(x.ProductName || '') === 'Battery')
                    .map((adder: any) => ({
                        ...adder,
                        Value: adder.Value,
                        Text: (adder.FreeQty && adder.FreeQty > 0)
                            ? `${adder.Text} (Up to ${adder.FreeQty} included in price)`
                            : adder.Text,
                        Group: adder.Group,
                        UnitType: adder.UnitType,
                        Qty: adder.Qty || 0,
                        UnitPrice: adder.UnitPrice || 0,
                        FreeQty: adder.FreeQty || 0,
                        ServiceID: adder.ServiceCode ?? adder.ServiceID ?? '',
                        ServiceCode: adder.ServiceCode,
                        ProductTypeAdderID: adder.ProductTypeAdderID,
                        ProductTypeID: resolveProductTypeIdFromCategory(
                            adder.ProductName || COMMON_CONSTANTS.BatteryProductName,
                            productTypeList,
                            opportunityProducts,
                            adder.ProductTypeID ?? adder.Product_Type_Id ?? adder.product_Type_Id,
                        ),
                        ProductName: adder.ProductName,
                    }));
                if (!cancelled) setBatteryAdderCatalog(mapped);
            } catch {
                if (!cancelled) setBatteryAdderCatalog([]);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const adderSelectOptions = useMemo(() => {
        const inTable = new Set(adderRows.map((r) => r.item));
        const groups = new Map<string, BatteryAdderCatalogItem[]>();
        batteryAdderCatalog.forEach((a) => {
            if (a.Value === BATTERY_SETUP_ADDER_NAME || a.Text === BATTERY_SETUP_ADDER_NAME) return;
            const g = String(a.Group || 'UNGROUPED');
            if (!groups.has(g)) groups.set(g, []);
            groups.get(g)!.push(a);
        });
        return Array.from(groups.entries()).map(([label, items]) => ({
            label,
            options: items.map((a) => ({
                value: a.Value,
                label: a.Text,
                disabled: inTable.has(a.Value) || inTable.has(a.Text),
            })),
        }));
    }, [batteryAdderCatalog, adderRows]);

    const canViewCommission = hasCapability('Commission_Amount');
    const canToggleCommission = hasCapability('View_Commission_Amount');
    const isTeslaBattery =
        String(systemDesign?.BatteryInformation?.Manufacturer ?? priceQuote?.BatteryInformation?.Manufacturer ?? '') === 'Tesla';

    const getProductCode = useCallback(() => {
        const interest = systemDesign?.OpportunityInterest || opportunityDetails?.OpportunityInterest;
        return batteryCalculatorProductCode(interest);
    }, [systemDesign, opportunityDetails]);

    // Initial Hydration from systemDesign or confirmQuoteCellKey
    useEffect(() => {

        if (!confirmQuoteCellKey) return;

        const b = systemDesign?.Battery || {};
        const partnerName = confirmQuoteCellKey.partner;
        const matchedPartner = financePartnersList.find(p => p.Text.toLowerCase().replace(' ', '') === partnerName?.toLowerCase().replace(' ', ''));

        if (matchedPartner) {
            setFinancePartnerId(String(matchedPartner.Value));
            handlePartnerChange(String(matchedPartner.Value), true);
        }

        // const gridMethod = confirmQuoteCellKey.methord;
        // if (gridMethod) setFinanceMethodId(gridMethod);

        // Pre-fill other fields from systemDesign if available
        // V1 battery-detail-form does not seed PricingMethod from the grid finance method
        // (`Battery Lease` would be sent as PricingMethod and miss Excel SfdcSyncValue).
        setPrice(Number(confirmQuoteCellKey.price || 0));
        selectedGridTermRef.current = null;
        selectedGridEscalatorRef.current = null;
        const openedFromPricingOffer = !!(
            confirmQuoteCellKey.ExcelOffer
            || confirmQuoteCellKey.payload
            || isPositiveGridEscalator(confirmQuoteCellKey.escalator)
            || confirmQuoteCellKey.products?.some((p) => p.ExcelOffer || p.payload || isPositiveGridEscalator(p.escalator))
        );
        const offerLine =
            confirmQuoteCellKey.products?.find((p) => String(p.product ?? '').toLowerCase().includes('battery'))
            ?? confirmQuoteCellKey;
        // Pricing-offer click: use that offer's term/escalator. Direct Generate Quote: V1 dropdown + GetPricingLRBO.
        if (openedFromPricingOffer) {
            const gridTerm = offerLine.term ?? confirmQuoteCellKey.term;
            if (gridTerm != null && Number.isFinite(Number(gridTerm)) && Number(gridTerm) > 0) {
                const t = String(gridTerm);
                selectedGridTermRef.current = t;
                termRef.current = t;
                setTerm(t);
            }
            const gridEscalator = offerLine.escalator ?? confirmQuoteCellKey.escalator;
            if (isPositiveGridEscalator(gridEscalator)) {
                const e = String(gridEscalator);
                selectedGridEscalatorRef.current = e;
                escalatorRef.current = e;
                setEscalator(e);
            }
        }
        openedFromPricingOfferRef.current = openedFromPricingOffer;
        pricedBatteryEpcRef.current = null;
        pricedTierLabelRef.current = null;
        const pricedEpc = openedFromPricingOffer
            ? (pricedBatteryEpcFromQuote(offerLine) ?? pricedBatteryEpcFromQuote(confirmQuoteCellKey))
            : null;
        const labeledTier = namedTierFromPricingLabel(offerLine.tier ?? confirmQuoteCellKey.tier, isTeslaBattery);
        pricedBatteryEpcRef.current = pricedEpc;
        pricedTierLabelRef.current = labeledTier;
        if (isTeslaBattery) {
            if (openedFromPricingOffer) {
                if (pricedEpc) {
                    teslaPriceValueRef.current = pricedEpc;
                    setTeslaPriceValue(pricedEpc);
                } else {
                    teslaPriceValueRef.current = undefined;
                    setTeslaPriceValue(undefined);
                }
                if (labeledTier) setPriceTier(labeledTier);
            } else {
                teslaPriceValueRef.current = undefined;
                setTeslaPriceValue(undefined);
                setPriceTier('Base');
            }
        } else if (labeledTier) {
            setPriceTier(labeledTier);
        } else {
            setPriceTier('Standard');
        }

        // con
        // let termAndApr = ''
        // setTermApr( || null);
        // setRebate(Number(b.Rebate || 0));
        // setDownPayment(Number(b.DownPayment || 0));
        // setDepositAmount(Number(b.Deposit_Amount || 0));
        // setTax(Number(b.Tax || 0));
        // setDealerFee(Number(b.DealerFee || 0));
        // setPriceTier(systemDesign?.BatteryOnly?.PriceTier || 'Standard');
        // setSelectedAdders(systemDesign?.BatteryOnly?.BatteryAdder || []);
        // setAdditionalCost(Number(systemDesign?.BatteryOnly?.AdditionalCost || 0));

    }, [confirmQuoteCellKey]); // eslint-disable-line

    const handlePartnerChange = async (partnerId: string, resetFields: boolean = true) => {
        const partner = financePartnersList.find(p => String(p.Value) === partnerId);
        if (!partner) return;

        updateLoaderCount(true);
        try {
            const stateId = opportunityDetails?.InstallationAddress?.stateCode || systemDesign?.InstallationAddress?.StateId || '';
            const product = getProductCode();

            if (partner.Text === 'Sunnova') {
                const payload = {
                    ExcelFileName: "Battery Calculator",
                    SheetName: "Battery IO",
                    InputValues: [
                        { Type: "String", CellNumber: "C3", CellValue: stateId },
                        { Type: "String", CellNumber: "C4", CellValue: product },
                        { Type: "String", CellNumber: "C7", CellValue: partner.Text }
                    ],
                    OutputCells: ["F14", "F15"]
                };
                const res = await masterServices.cascadingDropDown(payload);
                if (res) {
                    const methods = String(res[0]?.CellValue || '').split('|').filter(Boolean).map(v => ({ value: v.trim(), label: v.trim() }));
                    const terms = String(res[1]?.CellValue || '').split('|').filter(Boolean).map(v => ({ value: v.trim(), label: v.trim() }));
                    setFinanceMethods(methods.reverse());
                    setTermAprOptions(terms);
                    if (resetFields && methods.length > 0) setFinanceMethodId(methods[0].value);
                    if (resetFields && terms.length > 0) {
                        setTermApr((prev) => (prev && terms.some((t) => t.value === prev) ? prev : terms[0].value));
                    }
                }
            } else {
                if (!confirmQuoteCellKey?.methord || confirmQuoteCellKey?.methord == 'CASH') {
                    const payload = {
                        ExcelFileName: "Sunnova Financial Data",
                        SheetName: "SVA Financing Options",
                        InputValues: [
                            { Type: "String", CellNumber: "B4", CellValue: stateId },
                            { Type: "String", CellNumber: "C4", CellValue: product },
                            { Type: "String", CellNumber: "D4", CellValue: partner.Text },
                            { Type: "String", CellNumber: "E8", CellValue: systemDesign?.SolarUtility?.UtilityCompanyName || '' }
                        ],
                        OutputCells: ["E4"]
                    };
                    const res = await masterServices.cascadingDropDown(payload);
                    if (res) {
                        const methods = String(res[0]?.CellValue || '').split('|').filter(Boolean).map(v => ({ value: v.trim(), label: v.trim() }));
                        setFinanceMethods(methods);
                        if (resetFields && methods.length > 0) setFinanceMethodId(methods[0].value);
                    }
                } else {
                    setFinanceMethods([{ value: confirmQuoteCellKey?.methord, label: confirmQuoteCellKey?.methord }]);
                    setFinanceMethodId(confirmQuoteCellKey?.methord);
                }

            }
        } catch (err) {
        } finally {
            updateLoaderCount(false);
        }
    };

    const getFormValue = (ele: string): string => {
        switch (ele) {
            case 'Term & APR':
                return termApr ?? '';
            case 'Term':
                return term ?? '';
            case 'Deposit Amount':
            case 'Deposit':
                return String(depositAmount);
            case 'Rebate':
                return String(rebate);
            case 'Down Payment':
                return String(downPayment);
            case 'Tax':
                return String(tax);
            case 'Dealer Fee':
                return String(dealerFee);
            case 'Upon Completion of Physical Install':
                return String(uponCompletionOfPhysicalInstall);
            case 'Upon Completion of Work':
                return String(uponCompletionOfWork);
            default:
                return '';
        }
    };

    const setAmount = (ele: string, value: number) => {
        switch (ele) {
            case 'Deposit Amount':
            case 'Deposit':
                setDepositAmount(value);
                break;
            case 'Rebate':
                setRebate(value);
                break;
            case 'Down Payment':
                setDownPayment(value);
                break;
            case 'Tax':
                setTax(value);
                break;
            case 'Dealer Fee':
                setDealerFee(value);
                break;
            case 'Upon Completion of Physical Install':
                setUponCompletionOfPhysicalInstall(value);
                break;
            case 'Upon Completion of Work':
                setUponCompletionOfWork(value);
                break;
            default:
                break;
        }
    };



    useEffect(() => {
        if (!financePartnerId || !financeMethodId) return;

        const fetchMethodDetails = async () => {
            const partner = financePartnersList.find(p => String(p.Value) === financePartnerId);
            if (!partner) return;

            updateLoaderCount(true);
            try {
                const stateId = opportunityDetails?.InstallationAddress?.stateCode || systemDesign?.InstallationAddress?.StateId || '';
                const product = getProductCode();
                const payload = {
                    ExcelFileName: "Sunnova Financial Data",
                    SheetName: "SVA Financing Options",
                    InputValues: [
                        { Type: "String", CellNumber: "B4", CellValue: stateId },
                        { Type: "String", CellNumber: "C4", CellValue: product },
                        { Type: "String", CellNumber: "D4", CellValue: partner.Text },
                        { Type: "String", CellNumber: "F4", CellValue: financeMethodId },
                        { Type: "String", CellNumber: "E8", CellValue: systemDesign?.SolarUtility?.UtilityCompanyName || '' }
                    ],
                    OutputCells: ["G4", "N4", "H4", "L4", "I4", "L14"]
                };

                const res = await masterServices.cascadingDropDown(payload);
                if (res) {

                    let enabledFields = res[0].CellValue.split('|').map((v: any) => v.trim());
                    if(partner.Text == "Cash"){
                        enabledFields.push("Deposit");
                        enabledFields.push("Upon Completion of Physical Install");
                        enabledFields.push("Upon Completion of Work");
                    }

                    if (partner.Text.toLowerCase() != "goodleap") {
                        enabledFields.push("Deposit");
                    }

                    setEnabledFields(enabledFields);
                    const pNames = String(res[2]?.CellValue || '').split('|');
                    const pValues = String(res[4]?.CellValue || '').split('|');
                    setPricingMethods(pNames.map((n, i) => ({ value: pValues[i]?.trim() || n.trim(), label: n.trim() })));

                    const terms = String(res[1]?.CellValue || '')
                        .split('|')
                        .filter(Boolean)
                        .map((v) => ({ value: v.trim(), label: v.trim() }));

                    if (terms.length > 0) {
                        setTermAprOptions(terms);

                        if (partner.Text === 'Good Leap' || partner.Text === 'Standard') {
                            const cellTerm = Number(confirmQuoteCellKey?.term);
                            const cellApr = Number(confirmQuoteCellKey?.apr);
                            let selectedMatchedValue = '';

                            if (!Number.isNaN(cellTerm) && !Number.isNaN(cellApr)) {
                                const target = `${cellTerm} years at ${cellApr}%`.toLowerCase();
                                const found = terms.find((t) => {
                                    const v = t.value.toLowerCase();
                                    if (v === target) return true;
                                    const nums = v.match(/\d+(\.\d+)?/g)?.map(Number);
                                    return nums?.includes(cellTerm) && nums?.includes(cellApr);
                                });
                                if (found) selectedMatchedValue = found.value;
                            }

                            if (selectedMatchedValue) {
                                setTermApr(selectedMatchedValue);
                            } else {
                                setTermApr((prev) => {
                                    if (prev && terms.some((t) => t.value === prev)) return prev;
                                    return terms[0].value;
                                });
                            }
                        } else {
                            setTermApr((prev) => {
                                if (prev && terms.some((t) => t.value === prev)) return prev;
                                return terms[0].value;
                            });
                        }
                    }

                    const escs = String(res[3]?.CellValue || '').split('|').filter(Boolean).map(v => ({ value: v.trim(), label: v.trim() }));
                    setEscalatorOptions(escs);
                    escalatorOptionsRef.current = escs;
                    const isLightreachBatteryLease = partner?.Text == COMMON_CONSTANTS.LightreachPartnerName && String(financeMethodId ?? '').includes('Battery Lease');
                    setEscalator((prev) => {
                        const fromPlan = isPositiveGridEscalator(selectedGridEscalatorRef.current)
                            ? matchSelectOptionByNumber(escs, selectedGridEscalatorRef.current)
                            : null;
                        if (fromPlan) {
                            escalatorRef.current = fromPlan;
                            return fromPlan;
                        }
                        if (isLightreachBatteryLease) {
                            return pickBatteryEscalatorLikeV1(
                                true,
                                lrboProductsRef.current.length ? lrboProductsRef.current : lightreachEligibleProductDataForBattery,
                                escs,
                                solarMasterSync?.escalator,
                            ) ?? prev;
                        }
                        if (prev && prev !== 'undefined' && escs.some((o) => o.value === prev)) return prev;
                        return escs[0]?.value ?? null;
                    });

                    const termops = String(res[5]?.CellValue || '').split('|').filter(Boolean).map(v => ({ value: v.trim(), label: v.trim() }));
                    setTermOptions(termops);
                    if (isLightreachBatteryLease) {
                        const fromPlan = matchSelectOptionByNumber(termops, selectedGridTermRef.current);
                        const nextTerm = fromPlan ?? termops[0]?.value ?? '';
                        if (nextTerm) termRef.current = nextTerm;
                        setTerm(nextTerm);
                    }
                }
            } catch (err) {
                console.error("Method Change Error:", err);
            } finally {
                updateLoaderCount(false);
            }
        };

        fetchMethodDetails();
    }, [financeMethodId, financePartnerId]); // eslint-disable-line

    const applyLrBoMonthlyFromOffer = (offer: unknown) => {
        const monthly = year1TotalMonthlyFromLrBoOffer(offer);
        if (monthly != null) {
            setDetailValue((prev: any) => (
                prev?.Monthly_Finance_Partner_Payment === monthly
                    ? prev
                    : { ...prev, Monthly_Finance_Partner_Payment: monthly }
            ));
        }
        const productId = productIdFromLrBoOffer(offer);
        if (productId) setOfferId(productId);
    };

    const updateLightreachOfferAndPayment = (eligibleProducts: any[], currentAmount?: number) => {
        const partner = financePartnersList.find(p => String(p.Value) === financePartnerId);
        const isLightreachBatteryLease = partner?.Text == 'Lightreach' && financeMethodId?.includes('Battery Lease');
        if (!isLightreachBatteryLease) return;

        if (eligibleProducts && eligibleProducts.length > 0) {
            const matchedProduct = matchLrboProduct(eligibleProducts, escalatorRef.current, termRef.current);
            if (matchedProduct) {
                applyLrBoMonthlyFromOffer(matchedProduct);
                return;
            }
        }

        const planTotal = pricedBatteryTotalFromQuote(confirmQuoteCellKey);
        if (currentAmount != null && planTotal != null && !amountsMatch(currentAmount, planTotal)) {
            return;
        }

        applyLrBoMonthlyFromOffer(
            confirmQuoteCellKey?.payload ?? confirmQuoteCellKey?.ExcelOffer ?? confirmQuoteCellKey,
        );
    };

    const getEligibleProductForBatteryLightreach = async (grossSalesPrice: number) => {
        const amount = Number(grossSalesPrice);
        if (!Number.isFinite(amount) || amount <= 0) return;
        if (lastLrboAmountRef.current === amount) {
            updateLightreachOfferAndPayment(
                lrboProductsRef.current.length ? lrboProductsRef.current : lightreachEligibleProductDataForBattery,
                amount,
            );
            return;
        }
        lastLrboAmountRef.current = amount;
        const oid =
            opportunityDetails?.Opportunity_Id ??
            (useOneButtonStore.getState().opportunityID as string | number | undefined);
        const od = useOneButtonStore.getState().opportunityDetails ?? opportunityDetails;
        let leadId =
            lightreachBatteryLeaseLeadId
            || lrboLeadIdRef.current
            || getLightreachBatteryLeaseLeadIdFromOpportunity(od);
        if (!leadId && oid != null && String(oid).trim() !== '') {
            leadId = await resolveLightreachBatteryLeaseLeadId(od, oid);
            if (leadId) lrboLeadIdRef.current = leadId;
        }
        if (oid == null || String(oid).trim() === '' || !leadId) {
            lastLrboAmountRef.current = null;
            updateLightreachOfferAndPayment(lightreachEligibleProductDataForBattery, amount);
            return;
        }

        let res: unknown = [];
        try {
            res = await quoteServices.getEligibleProductForBatteryLightreach(oid, leadId, amount);
        } catch {
            lastLrboAmountRef.current = null;
            return;
        }
        const rows = lrBoOffersFromResponse(res);
        if (rows.length > 0) {
            setLightreachEligibleProductDataForBattery(rows);
            lrboProductsRef.current = rows;
            updateLightreachOfferAndPayment(rows, amount);
            const isCurrentSelectionValid = !!matchLrboProduct(rows, escalatorRef.current, termRef.current);
            if (!isCurrentSelectionValid) {
                if (isPositiveGridEscalator(selectedGridEscalatorRef.current)) {
                    const selectedEscalatorStillInDropdown = !!matchSelectOptionByNumber(
                        escalatorOptionsRef.current,
                        selectedGridEscalatorRef.current,
                    );
                    if (!selectedEscalatorStillInDropdown) {
                        setEscalatorValue(rows);
                    }
                } else {
                    setEscalatorValue(rows);
                }
            }
        }
    };

    useEffect(() => {
        const partner = financePartnersList.find((p) => String(p.Value) === financePartnerId);
        const isLrBo = partner?.Text == 'Lightreach' && String(financeMethodId ?? '').includes('Battery Lease');
        if (!isLrBo) return;
        const amount = Number(detailValue.grossSalesPrice || 0) + (billableAdderCost || 0);
        if (!Number.isFinite(amount) || amount <= 0) return;
        void getEligibleProductForBatteryLightreach(amount);
    }, [billableAdderCost]); // eslint-disable-line

    useEffect(() => {
        updateLightreachOfferAndPayment(lightreachEligibleProductDataForBattery);
    }, [escalator, term, lightreachEligibleProductDataForBattery, financePartnerId, financeMethodId]);

    const setEscalatorValue = (products: any[]) => {
        const partner = financePartnersList.find(p => String(p.Value) === financePartnerId);
        const isLightreachBatteryLease = partner?.Text == 'Lightreach' && financeMethodId?.includes('Battery Lease');
        const planEscalator = isPositiveGridEscalator(selectedGridEscalatorRef.current)
            ? selectedGridEscalatorRef.current
            : isPositiveGridEscalator(confirmQuoteCellKey?.escalator)
                ? confirmQuoteCellKey?.escalator
                : null;
        const fromPlan = planEscalator != null
            ? matchSelectOptionByNumber(escalatorOptionsRef.current, planEscalator)
            : null;
        if (fromPlan) {
            escalatorRef.current = fromPlan;
            setEscalator(fromPlan);
            updateLightreachOfferAndPayment(products);
            return;
        }
        const next = pickBatteryEscalatorLikeV1(
            !!isLightreachBatteryLease,
            products,
            escalatorOptionsRef.current,
            solarMasterSync?.escalator,
        );
        if (next != null) {
            escalatorRef.current = next;
            setEscalator(next);
            updateLightreachOfferAndPayment(products);
        }
    };

    const updateCalculations = useCallback(async () => {
        if (!financePartnerId || !financeMethodId) return;
        if (calculationInFlightRef.current) return;
        calculationInFlightRef.current = true;
        const partner = financePartnersList.find(p => String(p.Value) === financePartnerId);
        const depositAmountSnapshot = depositAmountRef.current;
        updateLoaderCount(true);
        try {

            const stateId =
                priceQuote?.InstallationState ||
                opportunityDetails?.InstallationAddress?.State ||
                systemDesign?.InstallationAddress?.State ||
                opportunityDetails?.InstallationAddress?.stateCode ||
                systemDesign?.InstallationAddress?.StateId ||
                '';
            const product = getProductCode();
            const bi = priceQuote?.BatteryInformation || systemDesign?.BatteryInformation || {};
            const isLrBo = partner?.Text == 'Lightreach' && String(financeMethodId ?? '').includes('Battery Lease');
            let teslaC15 = teslaPriceValueRef.current ?? teslaPriceValue;

            const runBatteryCalculator = (teslaC15Value: number | undefined) =>
                masterServices.cascadingDropDown({
                ExcelFileName: "Battery Calculator",
                SheetName: "Battery IO",
                InputValues: [
                    { Type: "String", CellNumber: "C3", CellValue: stateId },
                    { Type: "String", CellNumber: "C4", CellValue: product },
                    { Type: "String", CellNumber: "C5", CellValue: bi.Manufacturer || '' },
                    { Type: "String", CellNumber: "C6", CellValue: bi.Quantity != null && bi.Quantity !== '' ? String(bi.Quantity) : '' },
                    { Type: "String", CellNumber: "C7", CellValue: partner?.Text || '' },
                    { Type: "String", CellNumber: "C8", CellValue: financeMethodId },
                    { Type: "String", CellNumber: "C9", CellValue: termApr || '' },
                    { Type: "String", CellNumber: "C13", CellValue: '' },
                    { Type: "String", CellNumber: "C14", CellValue: String(depositAmountSnapshot) },
                    isTeslaBattery
                        ? { Type: "String", CellNumber: "C15", CellValue: teslaC15Value != null ? String(teslaC15Value) : '' }
                        : { Type: "String", CellNumber: "C10", CellValue: priceTierRef.current },
                ],
                OutputCells: isTeslaBattery
                    ? ["F5", "F7", "F8", "F9", "F10", "F11", "F12", "F13", "F6", "AF4", "AF3", "F16", "F17"]
                    : ["F5", "F7", "F8", "F9", "F10", "F11", "F12", "F13", "F6", "AF4", "AF3", "F16"]
            });

            let res = await runBatteryCalculator(teslaC15);
            if (res) {
                const readTeslaTiers = (rows: any[]) => {
                    let teslaMin = 0;
                    let teslaBase = 0;
                    if (!isTeslaBattery) return { teslaMin, teslaBase };
                    const f17 = rows.find((o: any) => o.CellNumber === 'F17')?.CellValue;
                    if (f17 && f17 !== 'NA' && f17 !== 0) {
                        try {
                            const minBaseTiers = typeof f17 === 'string' ? JSON.parse(f17) : f17;
                            teslaMin = Number(minBaseTiers?.[1]?.Output?.Minimum ?? 0);
                            teslaBase = Number(minBaseTiers?.[1]?.Output?.Base ?? 0);
                        } catch {
                            teslaMin = 0;
                            teslaBase = 0;
                        }
                    }
                    return { teslaMin, teslaBase };
                };
                let { teslaMin, teslaBase } = readTeslaTiers(res);
                if (isTeslaBattery && teslaC15 == null && teslaBase) {
                    if (openedFromPricingOfferRef.current && pricedBatteryEpcRef.current) {
                        teslaC15 = pricedBatteryEpcRef.current;
                    } else if (openedFromPricingOfferRef.current && pricedTierLabelRef.current === 'Min' && teslaMin) {
                        teslaC15 = teslaMin;
                        setPriceTier('Min');
                    } else {
                        teslaC15 = teslaBase;
                        setPriceTier('Base');
                    }
                    teslaPriceValueRef.current = teslaC15;
                    setTeslaPriceValue(teslaC15);
                    const followUp = await runBatteryCalculator(teslaC15);
                    if (followUp) {
                        res = followUp;
                        ({ teslaMin, teslaBase } = readTeslaTiers(res));
                    }
                }
                const getVal = (cell: string) => res.find((o: any) => o.CellNumber === cell)?.CellValue || 0;
                const excelMonthly = Number(getVal('F6'));
                const gross = Number(getVal('F5'));
                const matchedLr = isLrBo
                    ? matchLrboProduct(lrboProductsRef.current, escalatorRef.current, termRef.current)
                    : undefined;
                const lrMonthly = matchedLr ? lrboMonthly(matchedLr) : NaN;
                const planMonthly = isLrBo
                    ? (year1TotalMonthlyFromLrBoOffer(confirmQuoteCellKey?.payload)
                        ?? year1TotalMonthlyFromLrBoOffer(confirmQuoteCellKey))
                    : null;
                const planTotal = isLrBo ? pricedBatteryTotalFromQuote(confirmQuoteCellKey) : null;
                const usePlanMonthly = Number(planMonthly) > 0 && amountsMatch(gross, planTotal);
                const monthly = Number.isFinite(lrMonthly)
                    ? lrMonthly
                    : (usePlanMonthly ? Number(planMonthly) : excelMonthly);
                setDetailValue({
                    grossSalesPrice: Number(getVal('F5')),
                    totalSalesCommission: Number(getVal('F7')),
                    floorTierPrice: isTeslaBattery ? 0 : Number(getVal('F8')),
                    minTierPrice: isTeslaBattery ? teslaMin : Number(getVal('F9')),
                    midTierPrice: isTeslaBattery ? 0 : Number(getVal('F10')),
                    stdTierPrice: isTeslaBattery ? 0 : Number(getVal('F11')),
                    baseTierPrice: teslaBase,
                    batteryCostSubTotal: Number(getVal('F12')),
                    totalAdderCost: Number(getVal('F13')),
                    Financed_Amount: Number(getVal('F16')),
                    Monthly_Finance_Partner_Payment: monthly
                });

                if(Number(depositAmountSnapshot) > Number(getVal('F5'))){
                    updateErrorMessage(`The entered Deposit Amount cannot be greater than ${Number(getVal('F5')).toFixed(2)}`);
                }

                lastCalcCurrencyRef.current = {
                    depositAmount: depositAmountSnapshot,
                };

                const hasSolar = !!(
                    opportunityDetails?.OpportunityInterest?.Solar
                    || systemDesign?.OpportunityInterest?.Solar
                );
                const isBatteryOnlyInstall = !hasSolar && (
                    product === 'B'
                    || product === 'BR'
                    || !!opportunityDetails?.OpportunityInterest?.BatteryOnly
                    || !!systemDesign?.OpportunityInterest?.BatteryOnly
                );
                const setupAmount = isBatteryOnlyInstall
                    ? Math.max(0, Number((Number(getVal('F5')) - Number(getVal('F12'))).toFixed(2)))
                    : 0;
                setAdderRows((prev) => totalsFromAdderRows(syncSetupAdderRow(prev, setupAmount)).rows);

                if (partner?.Text == 'Good Leap') {
                    setPrice(detailValue.batteryCostSubTotal);
                }

                if (openedFromPricingOfferRef.current) {
                    const pricedAmount = teslaPriceValueRef.current ?? pricedBatteryEpcRef.current;
                    if (isTeslaBattery) {
                        if (pricedAmount != null && pricedAmount > 0) {
                            const matched = matchPricedAmountToTier(pricedAmount, [
                                { key: 'Min', price: teslaMin },
                                { key: 'Base', price: teslaBase },
                            ]);
                            const nextTier = matched ?? 'Custom';
                            setPriceTier((prev) => (prev === nextTier ? prev : nextTier));
                            if (!matched) setTeslaCustomAmount(pricedAmount);
                        }
                    } else if (pricedAmount != null && pricedAmount > 0) {
                        const matched = matchPricedAmountToTier(pricedAmount, [
                            { key: 'Floor', price: Number(getVal('F8')) },
                            { key: 'Min', price: Number(getVal('F9')) },
                            { key: 'Mid', price: Number(getVal('F10')) },
                            { key: 'Standard', price: Number(getVal('F11')) },
                        ]);
                        if (matched) setPriceTier((prev) => (prev === matched ? prev : matched));
                    }
                }

                if (partner?.Text == 'Lightreach' && String(financeMethodId ?? '').includes('Battery Lease')) {
                    await getEligibleProductForBatteryLightreach(Number(getVal('F5')) + (billableAdderCostRef.current || 0));
                }

                // if(this.batteryOnlyDetails.BatteryAdder.length>0) {
                //     let batteryIndex = this.batteryOnlyDetails.BatteryAdder.findIndex((el:any) => el == 'Battery Fire Enclosure');
                //     let luminIndex = this.batteryOnlyDetails.BatteryAdder.findIndex((el:any) => el == 'Lumin Smart Panel');
                //     if(batteryIndex>-1) this.updateAdder('Battery Fire Enclosure', this.detailValue.totalAdderCost, this.detailValue.totalAdderCost);
                //     else this.removeArray('Battery Fire Enclosure')
                //     if(luminIndex>-1) this.updateAdder('Lumin Smart Panel' , this.detailValue.totalAdderCost, this.detailValue.totalAdderCost);
                //     else this.removeArray('Lumin Smart Panel')
                // } else {
                //     this.BatteryAddersCost = []
                //     let defaultIndex = this.BatteryAddersCost.findIndex((el:any) => el.adderSelect == '');
                //     if(defaultIndex>-1){
                //         this.BatteryAddersCost[defaultIndex].amount =  this.batteryOnlyDetails.AdditionalCost;
                //         this.BatteryAddersCost[defaultIndex].pricing_Type =  this.batteryOnlyDetails.PriceTier;
                //     }else{
                //         this.BatteryAddersCost.push(
                //         {
                //         adderSelect: '',
                //         Adder_Cost: 0,
                //         amount: this.batteryOnlyDetails.AdditionalCost,
                //         pricing_Type: this.batteryOnlyDetails.PriceTier
                //         });
                //     }
                // }
            }
        } catch (err) {
            console.error("Calculation Error:", err);
        } finally {
            calculationInFlightRef.current = false;
            updateLoaderCount(false);
        }
        if (partner?.Text == 'Good Leap') goodLeapDropDownChange();
    }, [financePartnerId, financeMethodId, termApr, teslaPriceValue, isTeslaBattery]); // eslint-disable-line

    /** Runs Battery Calculator when deposit amounts change; skips if values match last successful run. */
    const commitBatteryCalculatorIfValuesChanged = useCallback(() => {
        const dep = depositAmountRef.current;
        const last = lastCalcCurrencyRef.current;
        if (last !== null && dep === last.depositAmount) {
            return;
        }
        void updateCalculations();
    }, [updateCalculations]);

    const parseCurrencyInput = (value: string | undefined) => {
        if (value === undefined || value === '') return 0;
        const n = Number(String(value).replace(/,/g, ''));
        return Number.isFinite(n) ? n : 0;
    };

    const handleCurrencyAmountChange = (ele: string, value: string | undefined) => {
        setAmount(ele, parseCurrencyInput(value));
    };

    useEffect(() => {
        updateCalculations();
    }, [updateCalculations]);

    useEffect(() => {
        if (isTeslaBattery) return;
        void updateCalculations();
    }, [priceTier]); // eslint-disable-line

    useEffect(() => {
        if (!solarMasterSync?.enabled) return;

        const syncedTermApr = String(solarMasterSync.termApr ?? '').trim();
        if (syncedTermApr && termAprOptions.some((o) => o.value === syncedTermApr) && termApr !== syncedTermApr) {
            setTermApr(syncedTermApr);
        }

        const syncedPricingMethod = String(solarMasterSync.pricingMethod ?? '').trim();
        if (syncedPricingMethod && pricingMethods.length > 0) {
            const matchedPricingMethod = pricingMethods.find(
                (o) => String(o.value).trim() === syncedPricingMethod || String(o.label).trim() === syncedPricingMethod,
            );
            if (matchedPricingMethod && pricingMethod !== matchedPricingMethod.value) {
                setPricingMethod(matchedPricingMethod.value);
            }
        }

        const partner = financePartnersList.find((p) => String(p.Value) === financePartnerId);
        const isLightreachBatteryLease =
            partner?.Text == COMMON_CONSTANTS.LightreachPartnerName &&
            String(financeMethodId ?? '').includes('Battery Lease');
        const syncedEscalator = String(solarMasterSync.escalator ?? '').trim();
        if (isLightreachBatteryLease && escalatorOptions.length > 0 && !isPositiveGridEscalator(escalator)) {
            const next = pickBatteryEscalatorLikeV1(
                true,
                lrboProductsRef.current.length ? lrboProductsRef.current : lightreachEligibleProductDataForBattery,
                escalatorOptions,
                syncedEscalator,
            );
            if (next && next !== escalator) {
                escalatorRef.current = next;
                setEscalator(next);
            }
            return;
        }
        if (!isLightreachBatteryLease && syncedEscalator && escalatorOptions.length > 0) {
            const matchedEscalator = escalatorOptions.find(
                (o) => String(o.value).trim() === syncedEscalator || String(o.label).trim() === syncedEscalator,
            );
            if (matchedEscalator && escalator !== matchedEscalator.value) {
                setEscalator(matchedEscalator.value);
                if(financePartnerId){
                    const partner = financePartnersList.find(p => String(p.Value) === financePartnerId);
                    if (partner?.Text == 'Good Leap') goodLeapDropDownChange();
                }
            }
        }
    }, [
        solarMasterSync,
        termAprOptions,
        pricingMethods,
        escalatorOptions,
        termApr,
        pricingMethod,
        escalator,
        financePartnerId,
        financeMethodId,
    ]);

    const goodLeapDropDownChange = async () => {
        try {

            const stateId = opportunityDetails?.InstallationAddress?.stateCode || systemDesign?.InstallationAddress?.StateId || '';
            const product = getProductCode();
            const bi = systemDesign?.BatteryInformation || {};
            const partner = financePartnersList.find(p => String(p.Value) === financePartnerId);
            updateLoaderCount(true);
            const payload = {
                ExcelFileName: "Sunnova Financial Data",
                SheetName: "SVA Financing Options",
                InputValues: [

                    {
                        "Type": "String",
                        "CellNumber": "B4",
                        "CellValue": stateId
                    },
                    {
                        "Type": "String",
                        "CellNumber": "C4",
                        "CellValue": product
                    },
                    {
                        "Type": "String",
                        "CellNumber": "D4",
                        "CellValue": partner?.Text || ''
                    },
                    {
                        "Type": "String",
                        "CellNumber": "F4",
                        "CellValue": financeMethodId
                    },
                    {
                        "Type": "String",
                        "CellNumber": "K4",
                        "CellValue": 0
                    },
                    {
                        "Type": "String",
                        "CellNumber": "M4",
                        "CellValue": ''
                    },
                    {
                        "Type": "String",
                        "CellNumber": "O4",
                        "CellValue": termApr || ''
                    },
                    {
                        "Type": "String",
                        "CellNumber": "E8",
                        "CellValue": systemDesign?.SolarUtility?.UtilityCompanyName || ''
                    }
                ],
                OutputCells: ["T4", "U11"]
            };

            const res = await masterServices.cascadingDropDown(payload);
            if (res) {
                updateLoaderCount(false);
                setOfferId(res[0].CellValue);
            }
        } catch (err) {
            console.error("Calculation Error:", err);
        } finally {
            updateLoaderCount(false);
        }
    }


    const addSelectedBatteryAdders = () => {
        if (!selectedAdderValues.length || addersReadOnly) return;
        const existing = new Set(adderRows.map((r) => r.item));
        const newRows: BatteryAdderRow[] = [];
        selectedAdderValues.forEach((value) => {
            if (existing.has(value) || value === BATTERY_SETUP_ADDER_NAME) return;
            const adderItem = batteryAdderCatalog.find((a) => a.Value === value);
            if (!adderItem) return;
            const quantity = Number(adderItem.Qty) || 0;
            const unitPrice = Number(adderItem.UnitPrice) || 0;
            const freeQty = Number(adderItem.FreeQty) || 0;
            newRows.push({
                id: adderIdRef.current++,
                item: value,
                name: String(adderItem.Text || value).replace(/\s*\(Up to \d+ included in price\)\s*$/, ''),
                ServiceID: adderItem.ServiceID || adderItem.ServiceCode || '',
                quantity,
                unitPrice,
                freeQty,
                subTotal: Math.max(0, quantity - freeQty) * unitPrice,
                productTypeAdderID: Number(adderItem.ProductTypeAdderID) || 0,
                productTypeID: resolveProductTypeIdFromCategory(
                    adderItem.ProductName || COMMON_CONSTANTS.BatteryProductName,
                    productTypeList,
                    opportunityProducts,
                    adderItem.ProductTypeID ?? adderItem.Product_Type_Id ?? adderItem.product_Type_Id ?? batteryProductTypeId,
                ),
            });
            existing.add(value);
        });
        if (!newRows.length) return;
        setAdderRows((prev) => totalsFromAdderRows([...prev, ...newRows]).rows);
    };

    const removeBatteryAdder = (id: string | number) => {
        if (addersReadOnly) return;
        setAdderRows((prev) => {
            const row = prev.find((r) => r.id === id);
            if (row?.isFixed) return prev;
            if (row) {
                setSelectedAdderValues((vals) => vals.filter((v) => v !== row.item));
            }
            return totalsFromAdderRows(prev.filter((r) => r.id !== id)).rows;
        });
    };

    const updateBatteryAdderRow = (id: string | number, patch: Partial<BatteryAdderRow>) => {
        if (addersReadOnly) return;
        setAdderRows((prev) => totalsFromAdderRows(prev.map((row) => {
            if (row.id !== id || row.isFixed) return row;
            const next = { ...row, ...patch };
            next.quantity = Number(next.quantity) || 0;
            if (next.quantity < 0) next.quantity = 0;
            next.unitPrice = Number(next.unitPrice) || 0;
            return next;
        })).rows);
    };

    const applyTeslaTier = (tier: string) => {
        setPriceTier(tier);
        if (tier === 'Min') setTeslaPriceValue(Number(detailValue.minTierPrice) || 0);
        else if (tier === 'Base') setTeslaPriceValue(Number(detailValue.baseTierPrice) || 0);
        else if (tier === 'Custom') setTeslaPriceValue(teslaCustomAmount);
    };

    const formatCurrency = (val: any) => {
        const num = Number(val || 0);
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(num);
    };

    const lightreachPartnerOnForm = financePartnersList.find((p) => String(p.Value) === financePartnerId)?.Text
        === COMMON_CONSTANTS.LightreachPartnerName;
    const isLightReachPartner = lightreachPartnerOnForm || (opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? []).some(
        (ele: any) =>
            (ele.Product_Type_Name ?? ele.product_Type_Name) === COMMON_CONSTANTS.BatteryProductName &&
            String(ele.Finance_Partner_Display_Name ?? ele.Finance_Partner ?? '')
                .toLowerCase()
                .replace(/\s+/g, '') === COMMON_CONSTANTS.LightreachPartnerName.toLowerCase(),
    );
    const isLightreachBatteryLeaseForm =
        lightreachPartnerOnForm && String(financeMethodId ?? '').includes('Battery Lease');

    const lightreachQuoteError = () => {
        if (!isLightReachPartner) return false;
        if (!lightreachPriceCapsReady) return false;
        if (lightreachPriceCapsData?.length === 0) return true;
        if (isLightreachBatteryLeaseForm && (!lightreachEligibleProductDataForBattery || lightreachEligibleProductDataForBattery.length === 0)) {
            return true;
        }
        return validateLightreachQuote() != null;
    };

    /** Cap error only when a matching price-cap row exists and total sales exceeds max × qty. */
    const validateLightreachQuote = (): 'cap' | 'escalator' | null => {
        if (!isLightReachPartner || !lightreachPriceCapsReady || !priceQuote) return null;
        if (isLightreachBatteryLeaseForm) {
            if (!lightreachEligibleProductDataForBattery || lightreachEligibleProductDataForBattery.length === 0) {
                return null;
            }
            const escalatorRaw = Number(escalator || 0);
            const matchedEligibleProduct = lightreachEligibleProductDataForBattery.find((product: any) =>
                lrboEscalationPct(product) === Number(escalatorRaw.toFixed(2)),
            );
            return matchedEligibleProduct ? null : 'escalator';
        }
        if (!lightreachPriceCapsData?.length) return null;
        const bi = priceQuote.BatteryInformation ?? systemDesign?.BatteryInformation ?? {};
        const batterycount = Number(bi.Quantity);
        const modelName = String(bi.FinancePartnerModelName ?? bi.ModelName ?? bi.Model ?? '').trim();
        const manufacturer = String(bi.Manufacturer ?? '').trim();
        const totalSalesAmount = Number(detailValue.grossSalesPrice || 0) + Number(billableAdderCost || 0);
        if (!batterycount || !modelName || !manufacturer || !totalSalesAmount) return null;
        const matchedProduct = lightreachPriceCapsData.find((price: any) => {
            const capModel = String(price.model ?? price.Model ?? '').trim();
            const capMfg = String(price.manufacturer ?? price.Manufacturer ?? '').trim();
            return capModel.toLowerCase() === modelName.toLowerCase() && capMfg.toLowerCase() === manufacturer.toLowerCase();
        });
        if (!matchedProduct) return null;
        const maxPrice = Number(matchedProduct.maxPrice ?? matchedProduct.MaxPrice ?? 0);
        if (!maxPrice) return null;
        const calculatedAmount = maxPrice * batterycount;
        return totalSalesAmount > calculatedAmount ? 'cap' : null;
    };

    const getBatteryValidationMessages = (): string[] => {
        const messages: string[] = [];
        if (!isLightReachPartner || !lightreachPriceCapsReady) return messages;
        if (lightreachPriceCapsData?.length === 0) {
            messages.push('No price cap available');
        }
        if (isLightreachBatteryLeaseForm && (!lightreachEligibleProductDataForBattery || lightreachEligibleProductDataForBattery.length === 0)) {
            messages.push('No eligible products found.');
        }
        const lightreachError = validateLightreachQuote();
        if (lightreachError === 'cap') {
            messages.push(
                'The Total Sales Amount exceeds the maximum Price Cap for the selected Finance Partner. Please review the Price Caps to select a valid option.',
            );
        } else if (lightreachError === 'escalator') {
            messages.push('No matched product found for the selected Escalator.');
        }
        return messages;
    };

    // Sync to store for GenerateQuoteModal
    useEffect(() => {
        const partner = financePartnersList.find(p => String(p.Value) === financePartnerId);
        const isBatteryLease = partner?.Text.toLocaleLowerCase() == COMMON_CONSTANTS.LightreachPartnerName.toLocaleLowerCase() &&
            financeMethodId?.includes('Battery Lease');
        let t: any;
        let apr: any;
        if (isBatteryLease) {
            t = term;
            apr = termApr;
        } else {
            [t, apr] = (termApr) ? termApr.replace("APR", "").split(" years at ") : ["0", "0"];
        }
        let currentPricingMethod = pricingMethod
        switch(financeMethodId){
            case 'PPA-EZ':
            case 'Lease':
                currentPricingMethod = 'EPC to Dealer ';
                break;
            case 'Loan': 
            case 'Loan Storage':
            case 'Battery Loan':
                currentPricingMethod = "Customer Price ";
                break;
            case 'Battery Lease':
                currentPricingMethod = pricingMethods[0]?.label || pricingMethods[0]?.value || pricingMethod || 'EPC';
                break;
            default:

        }

        updateBatteryDetails({
            financePartnerId,
            financePartnerName: partner?.Text || '',
            financeMethodName: financeMethodId,
            pricingMethod: currentPricingMethod,
            Escalator: escalator || '',
            price: price,
            termAndApr: termApr,
            term: Number(String(t).trim()),
            apr: Number(String(apr).replace("%", "").trim()),
            rebate,
            downPayment,
            depositAmount,
            tax,
            dealerFee,
            totalCommissionableAmount: detailValue.totalSalesCommission,
            grossSalesPrice: Number(detailValue.grossSalesPrice || 0) + billableAdderCost,
            financedAmount: Number(detailValue.Financed_Amount || 0) + billableAdderCost,
            monthlyPayment: detailValue.Monthly_Finance_Partner_Payment,
            Monthly_Finance_Partner_Payment: detailValue.Monthly_Finance_Partner_Payment,
            priceTier: priceTier,
            additionalCost: 0,
            addersList: userAddersPayload(adderRows),
            billableAdderCost,
            setupAdderCost: adderRows.find((r) => r.isFixed)?.subTotal || 0,
            totalAdderCost: billableAdderCost,
            valid: !!financePartnerId && !!financeMethodId && (termAprOptions.length > 0 ? !!termApr : true) && !lightreachQuoteError(),
            validationMessage: getBatteryValidationMessages().join('\n'),
            uponCompletionOfPhysicalInstall,
            uponCompletionOfWork,
            Offer_Id: offerId,
            lightreachBatteryEligibleProducts: lightreachEligibleProductDataForBattery,
        });
    }, [financePartnerId, financeMethodId, pricingMethod, pricingMethods, price, termApr, rebate, downPayment, depositAmount, tax, dealerFee, detailValue, priceTier, adderRows, billableAdderCost, financePartnersList, offerId, term, escalator, lightreachPriceCapsData, lightreachPriceCapsReady, lightreachEligibleProductDataForBattery]); // eslint-disable-line

    if (!confirmQuoteCellKey) return null;

    const batteryHeaderStats: GqSectionStat[] = [
        { label: 'Total sales amount', value: `$${formatCurrency(Number(detailValue.grossSalesPrice || 0) + billableAdderCost)}` },
        { label: 'Financed amount', value: `$${formatCurrency(Number(detailValue.Financed_Amount || 0) + billableAdderCost)}` },
        {
            label: 'Commission amount',
            value: `$${formatCurrency(detailValue.totalSalesCommission)}`,
            emphasize: true,
        },
    ];

    return (
        <div className="obd-roof-quote-details__container obd-roof-quote-details__container--split obd-gq-section">
            <GenerateQuoteSectionHead
                title="Battery"
                stats={batteryHeaderStats}
                ariaLabel="Battery quote totals"
            />
            <div className="obd-gq-section-body">
            <div className="obd-generate-quote-split obd-generate-quote-split--no-aside">
                <div className="obd-generate-quote-split__forms">
            <div className="obd-battery-quote-details__pair">
                <div className="obd-battery-quote-details__card">
                    <div className="obd-gq-product-line">
                        {confirmQuoteCellKey?.partner} — Battery 
                    </div>
                    <div className="obd-roof-quote-details__top-grid">
                        <div className="obd-roof-quote-details__field-group">
                            <label className="obd-roof-quote-details__label">Finance Method</label>
                            <OBDSelect
                                className="obd-roof-quote-details__select"
                                value={financeMethodId}
                                onChange={setFinanceMethodId}
                                options={financeMethods}
                                disabled
                                popupClassName="obd-select-dropdown"
                            />
                        </div>
                        {
                            ['Term & APR', 'Deposit Amount', 'Rebate', 'Term', 'Escalator', 'Down Payment', 'Tax', 'Dealer Fee', 'Deposit', "Upon Completion of Physical Install","Upon Completion of Work"].map(
                                (ele) => {
                                    if (enabledFields.includes(ele)) {
                                        return (
                                            <div key={ele} className="obd-roof-quote-details__field-group">
                                                <label className="obd-roof-quote-details__label">{ele === 'Deposit'?'Deposit Amount':ele}</label>
                                                {ele == "Term & APR" ? (
                                                    <OBDSelect
                                                        className="obd-roof-quote-details__select"
                                                        value={termApr ?? undefined}
                                                        onChange={(v) => setTermApr(v)}
                                                        options={termAprOptions}
                                                        placeholder={termAprOptions.length > 0 ? 'Select term' : 'No terms available'}
                                                        disabled={termAprOptions.length === 0 || Boolean(solarMasterSync?.enabled)}
                                                        popupClassName="obd-select-dropdown"
                                                        getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
                                                    />
                                                ) : (
                                                    ele === 'Pricing Method' ? (
                                                        <OBDSelect
                                                            className="obd-roof-quote-details__select"
                                                            value={pricingMethod ?? undefined}
                                                            onChange={(v) => setPricingMethod(v)}
                                                            options={pricingMethods}
                                                            disabled={Boolean(solarMasterSync?.enabled)}
                                                        />
                                                    ) : (

                                                    ele === 'Escalator' ? (
                                                        <OBDSelect
                                                            className="obd-roof-quote-details__select"
                                                            value={escalator ?? undefined}
                                                            onChange={(v) => setEscalator(v)}
                                                            options={escalatorOptions}
                                                            disabled={Boolean(solarMasterSync?.enabled)}
                                                        />
                                                    ) : (
                                                        ele === 'Term' ? (
                                                            <OBDSelect
                                                                className="obd-roof-quote-details__select"
                                                                value={term ?? undefined}
                                                                onChange={(v) => setTerm(v)}
                                                                options={termOptions}
                                                                disabled={Boolean(solarMasterSync?.enabled)}
                                                            />
                                                    ) : (

                                                        <CurrencyInput
                                                            className="obd-roof-quote-details__input"
                                                            value={Number(getFormValue(ele)).toFixed(2)}
                                                            onValueChange={(v) => handleCurrencyAmountChange(ele, v)}
                                                            onBlur={
                                                                ele === 'Deposit' || ele === 'Deposit Amount'
                                                                    ? commitBatteryCalculatorIfValuesChanged
                                                                    : undefined
                                                            }
                                                            prefix="$"
                                                            decimalsLimit={2}
                                                            fixedDecimalLength={2}
                                                            allowNegativeValue={false}
                                                        />
                                                ))))}
                                            </div>
                                        )
                                    }
                                }
                            )
                        }
                    </div>
                </div>

                <div className="obd-battery-quote-details__card">
                    <h3 className="obd-battery-quote-details__section-title">Choose battery price</h3>
                    <div
                        className="obd-battery-quote-details__battery-info"
                        aria-label="Battery system information"
                    >
                        <div className={`obd-battery-quote-details__field-group ${!systemDesign?.BatteryInformation?.Manufacturer ? 'error' : ''}`}>
                            <label className="obd-battery-quote-details__label">Battery Manufacturer</label>
                            <div className="obd-battery-quote-details__read-only">
                                {systemDesign?.BatteryInformation?.Manufacturer || 'Not selected'}
                            </div>
                        </div>
                        <div className={`obd-battery-quote-details__field-group ${!systemDesign?.BatteryInformation?.Quantity ? 'error' : ''}`}>
                            <label className="obd-battery-quote-details__label">Battery Count</label>
                            <div className="obd-battery-quote-details__read-only">
                                {systemDesign?.BatteryInformation?.Quantity || 0}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* III — pricing tiers */}
            <div className="obd-battery-quote-details__tier-section">
                <div className="obd-battery-quote-details__section-header">
                    <h3 className="obd-battery-quote-details__section-header-title">Select Pricing Tier</h3>
                    <div
                        className="obd-battery-quote-details__section-header-subtotal"
                        aria-label={`Battery cost subtotal ${formatCurrency(detailValue.batteryCostSubTotal)}`}
                    >
                        <span className="obd-battery-quote-details__subtotal-label">Battery Cost Subtotal</span>
                        <span className="obd-battery-quote-details__subtotal-value">
                            ${formatCurrency(detailValue.batteryCostSubTotal)}
                        </span>
                    </div>
                </div>
                <div className={`obd-battery-quote-details__tier-grid${isTeslaBattery ? ' obd-battery-quote-details__tier-grid--tesla' : ''}`}>
                    {isTeslaBattery ? (
                        <>
                            {detailValue.minTierPrice ? (
                                <div
                                    className={`obd-battery-quote-details__tier-card ${priceTier === 'Min' ? 'obd-battery-quote-details__tier-card--selected' : ''}`}
                                    onClick={() => applyTeslaTier('Min')}
                                >
                                    <span className="obd-battery-quote-details__tier-header">Minimum</span>
                                    <span className="obd-battery-quote-details__tier-value">${formatCurrency(detailValue.minTierPrice)}</span>
                                </div>
                            ) : null}
                            {detailValue.baseTierPrice ? (
                                <div
                                    className={`obd-battery-quote-details__tier-card ${priceTier === 'Base' ? 'obd-battery-quote-details__tier-card--selected' : ''}`}
                                    onClick={() => applyTeslaTier('Base')}
                                >
                                    <span className="obd-battery-quote-details__tier-header">Base</span>
                                    <span className="obd-battery-quote-details__tier-value">${formatCurrency(detailValue.baseTierPrice)}</span>
                                </div>
                            ) : null}
                            <div
                                className={`obd-battery-quote-details__tier-card obd-battery-quote-details__tier-card--custom ${priceTier === 'Custom' ? 'obd-battery-quote-details__tier-card--selected' : ''}`}
                                onClick={() => applyTeslaTier('Custom')}
                            >
                                <span className="obd-battery-quote-details__tier-header">Custom</span>
                                <CurrencyInput
                                    className="obd-battery-quote-details__input"
                                    value={Number(teslaCustomAmount).toFixed(2)}
                                    onValueChange={(v) => setTeslaCustomAmount(parseCurrencyInput(v))}
                                    onClick={(e) => e.stopPropagation()}
                                    onFocus={() => applyTeslaTier('Custom')}
                                    onBlur={() => {
                                        if (priceTier === 'Custom') setTeslaPriceValue(teslaCustomAmount);
                                    }}
                                    prefix="$"
                                    decimalsLimit={2}
                                    fixedDecimalLength={2}
                                    allowNegativeValue={false}
                                />
                            </div>
                        </>
                    ) : (
                        [
                            { label: 'Floor', value: 'Floor', price: detailValue.floorTierPrice },
                            { label: 'Min', value: 'Min', price: detailValue.minTierPrice },
                            { label: 'Mid', value: 'Mid', price: detailValue.midTierPrice },
                            { label: 'Standard', value: 'Standard', price: detailValue.stdTierPrice },
                        ].map(tier => (
                            <div
                                key={tier.value}
                                className={`obd-battery-quote-details__tier-card ${priceTier === tier.value ? 'obd-battery-quote-details__tier-card--selected' : ''}`}
                                onClick={() => setPriceTier(tier.value)}
                            >
                                <span className="obd-battery-quote-details__tier-header">{tier.label}</span>
                                <span className="obd-battery-quote-details__tier-value">${formatCurrency(tier.price)}</span>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Adders Selection — V1 CHOOSE BATTERY ADDERS */}
            <div className="obd-battery-quote-details__adders-section obd-roof-quote-details__adders-section">
            <div className="obd-battery-quote-details__pair obd-battery-quote-details__pair--adders">
                <div className="obd-battery-quote-details__card">
                    <h3 className="obd-roof-quote-details__adders-title">CHOOSE BATTERY ADDERS</h3>
                    <div className="obd-roof-quote-details__adder-controls-label">Battery Adder Type</div>
                    <div className="obd-roof-quote-details__adder-row">
                        <OBDSelect
                            className="obd-roof-quote-details__select obd-battery-quote-details__adder-select"
                            style={{ flex: 1 }}
                            mode="multiple"
                            maxTagCount={0}
                            maxTagPlaceholder={(omitted) => `+ ${omitted.length}`}
                            value={selectedAdderValues}
                            onChange={(val) => setSelectedAdderValues(val as string[])}
                            options={adderSelectOptions}
                            placeholder="Choose"
                            disabled={addersReadOnly}
                            optionFilterProp="label"
                            popupClassName="obd-select-dropdown"
                        />
                        <button
                            type="button"
                            className="obd-roof-quote-details__plus-btn"
                            onClick={addSelectedBatteryAdders}
                            disabled={addersReadOnly || selectedAdderValues.length === 0}
                            title="Add Item"
                        >
                            <Plus size={20} weight="bold" />
                        </button>
                    </div>
                </div>

                {adderRows.length > 0 ? (
                    <div className="obd-battery-quote-details__card obd-battery-quote-details__card--table">
                    <div className="obd-roof-quote-details__adder-table-wrap">
                        <table className="obd-roof-quote-details__adder-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Adder Name</th>
                                    <th>Unit Price</th>
                                    <th>Quantity</th>
                                    <th>Total</th>
                                    <th aria-label="Remove" />
                                </tr>
                            </thead>
                            <tbody>
                                {adderRows.map((row) => (
                                    <tr key={row.id} className="obd-roof-quote-details__adder-table-row">
                                        <td>
                                            <span className="obd-roof-quote-details__adder-item-name">{row.ServiceID}</span>
                                        </td>
                                        <td>
                                            <span className="obd-roof-quote-details__adder-item-name">{row.name || row.item}</span>
                                            {row.freeQty > 0 ? (
                                                <div className="obd-roof-quote-details__adder-item-note">
                                                    (Up to {row.freeQty} included in price)
                                                </div>
                                            ) : null}
                                        </td>
                                        <td>
                                            <CurrencyInput
                                                className="obd-roof-quote-details__input"
                                                value={Number(row.unitPrice).toFixed(2)}
                                                disabled={!!row.isFixed || addersReadOnly}
                                                onValueChange={(val) => updateBatteryAdderRow(row.id, { unitPrice: Number(val || 0) })}
                                                prefix="$"
                                                decimalsLimit={2}
                                                fixedDecimalLength={2}
                                                allowNegativeValue={false}
                                            />
                                        </td>
                                        <td>
                                            <Input
                                                type="number"
                                                min={0}
                                                className="obd-roof-quote-details__input obd-roof-quote-details__input--qty"
                                                value={row.quantity}
                                                disabled={!!row.isFixed || addersReadOnly}
                                                onChange={(e) => updateBatteryAdderRow(row.id, { quantity: Number(e.target.value) })}
                                                onBlur={(e) => {
                                                    const qty = Number(e.target.value);
                                                    updateBatteryAdderRow(row.id, { quantity: !e.target.value || qty < 0 ? 0 : qty });
                                                }}
                                            />
                                        </td>
                                        <td>
                                            <span className="obd-roof-quote-details__adder-cell-value">
                                                ${formatCurrency(row.subTotal)}
                                            </span>
                                        </td>
                                        <td className="obd-roof-quote-details__adder-table-actions">
                                            {!row.isFixed ? (
                                                <button
                                                    type="button"
                                                    className="obd-roof-quote-details__trash-btn"
                                                    onClick={() => removeBatteryAdder(row.id)}
                                                    disabled={addersReadOnly}
                                                    aria-label={`Remove ${row.item}`}
                                                >
                                                    <MinusCircle size={18} weight="fill" />
                                                </button>
                                            ) : null}
                                        </td>
                                    </tr>
                                ))}
                                <tr className="obd-battery-quote-details__adder-subtotal-row">
                                    <td colSpan={4} className="obd-battery-quote-details__adder-subtotal-label">
                                        <span className="obd-roof-quote-details__adder-item-name">Adder Cost Subtotal:</span>
                                    </td>
                                    <td className="obd-battery-quote-details__adder-subtotal-amount">
                                        <span className="obd-roof-quote-details__adder-cell-value obd-roof-quote-details__adder-cell-value--highlight">
                                            ${formatCurrency(adderDisplayTotal)}
                                        </span>
                                    </td>
                                    <td className="obd-battery-quote-details__adder-subtotal-actions" />
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    </div>
                ) : null}
            </div>
            </div>

                </div>
            </div>
            </div>
        </div>
    );
};

export default BatteryQuoteDetails;
